import { firebaseAuth } from "../../../firebase";
import { API_BASE } from "../../../API";
import {
  CustomerDetail,
  CustomerListResponse,
  CustomerStatsResponse,
  CustomerTextingResponse,
  DateRangeKey,
  DmAccountActivityResponse,
  DmFlagsResponse,
  DmMessagesResponse,
  DmThreadsResponse,
  EngagementStatsResponse,
  EventsCreatedResponse,
  EventsResponse,
  InstagramAccountsResponse,
  InstagramFunnelResponse,
  InstagramHistoryResponse,
  LeaderboardResponse,
  MessagingResponse,
  OverviewResponse,
  PaymentsResponse,
  PaymentStatusFilter,
  PlatformRevenueResponse,
  PlatformStatsResponse,
  PlatformVolumeResponse,
  ReferralsResponse,
  RevenueBasis,
  RevenueWindow,
  SyncStateResponse,
  CustomerVolumeResponse,
  VolumeLeaderboardResponse,
  VolumeWindow,
} from "./types";

// This tool talks to its backend with a plain fetch + the caller's Firebase
// ID token, not the shared typedAPI client — keeps this folder free of a
// dependency on a MarkitShared/@markit.common.api release cycle for a tool
// this self-contained. See this folder's README.
const requestJson = async <T>(path: string, method: "GET" | "POST" = "GET"): Promise<T> => {
  const token = await firebaseAuth.currentUser?.getIdToken();
  const res = await fetch(API_BASE + "admin/master-dashboard/" + path, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    // The backend sends { error } on every failure path; fall back to the
    // status code if the body isn't JSON (a proxy error, say).
    const message = await res
      .json()
      .then((body: { error?: string }) => body?.error)
      .catch(() => undefined);
    throw new Error(message ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
};

export const fetchOverview = (range: DateRangeKey) =>
  requestJson<OverviewResponse>(`overview?range=${encodeURIComponent(range)}`);

export const fetchCustomers = () => requestJson<CustomerListResponse>("customers");

export const fetchCustomer = (uid: string) =>
  requestJson<CustomerDetail>(`customers/${encodeURIComponent(uid)}`);

export const fetchCustomerTexting = (uid: string) =>
  requestJson<CustomerTextingResponse>(
    `customers/${encodeURIComponent(uid)}/texting`
  );

/** Cached — daily rows written by the nightly rollup. */
export const fetchPlatformStats = (range: DateRangeKey) =>
  requestJson<PlatformStatsResponse>(
    `platform/stats?range=${encodeURIComponent(range)}`
  );

/**
 * Live — one Twilio Usage Records call at the main account.
 *
 * Fetched separately from `/overview` on purpose: this one talks to a third
 * party, so a slow or failing Twilio should degrade only the two cards that
 * need it rather than blanking the whole Home tab.
 */
export const fetchMessaging = (range: DateRangeKey) =>
  requestJson<MessagingResponse>(`messaging?range=${encodeURIComponent(range)}`);

/**
 * Live — three collection-group counts (~400 documents, ~760ms measured).
 *
 * Fetched separately from the cached engagement stats below so the two land on
 * honest, separate clocks rather than one chip covering both.
 */
export const fetchInstagramFunnel = () =>
  requestJson<InstagramFunnelResponse>("engagement/instagram");

/**
 * Cached — signups and event attendance from the nightly rollup.
 *
 * Both are far too expensive to compute per page load: enumerating Firebase
 * Auth measured 241k accounts over 242 calls (~36s), and a 90-day event scan
 * measured ~250k ticket reads (~40s).
 */
export const fetchEngagementStats = (range: DateRangeKey) =>
  requestJson<EngagementStatsResponse>(
    `engagement/stats?range=${encodeURIComponent(range)}`
  );

/**
 * Live — the per-creator detail behind the funnel above.
 *
 * Measured for itself rather than assumed from the funnel's ~760ms, because
 * this one also joins creator accounts: 397 documents + a 35-creator batched
 * join, ~571ms total.
 */
export const fetchInstagramAccounts = () =>
  requestJson<InstagramAccountsResponse>("engagement/instagram/accounts");

/**
 * Cached — day-level history behind the three funnel steps.
 *
 * Cached where the funnel beside it is live, and the reason is not cost: one of
 * its four series cannot be computed on demand at all. `numTriggered` is a
 * lifetime counter with no per-fire timestamp anywhere in Firestore, so
 * fires-per-day only exists because the nightly job snapshots the total and the
 * read side differences consecutive days. The other three series come from
 * `connectedOn`/`createdAt` fields that were already on every document — those
 * are real history, and the response marks which is which.
 */
export const fetchInstagramHistory = () =>
  requestJson<InstagramHistoryResponse>("engagement/instagram/history");

/**
 * Live — 139 document reads fully expanded (7 referrers, 19 referees, 113
 * subscription documents), ~1.3s.
 *
 * The referrer query is a single-field inequality Firestore's automatic index
 * answers directly, so this never scans the `users` collection.
 */
export const fetchReferrals = () => requestJson<ReferralsResponse>("referrals");

/**
 * Cached — one read of the nightly per-event document.
 *
 * A live equivalent would have to re-read every event's tickets to know an
 * attendee count: 21k ticket reads at 30 days, 74.5k at 100. The nightly scan
 * already computes these exact rows, so caching them costs nothing.
 */
export const fetchEvents = () => requestJson<EventsResponse>("events");

/**
 * Cached — the "Events Created" board.
 *
 * A second document rather than an aggregation over `fetchEvents`, and that is
 * a measured decision rather than a structural preference: those rows come from
 * `events` queried by `start`, this from `createdAt`, and over the same 100
 * days 516 of the 1,146 products created in the window (45%) start beyond it and
 * appear in the other set not at all.
 */
export const fetchEventsCreated = () =>
  requestJson<EventsCreatedResponse>("events/created");

/**
 * Live — one `charges.search` cursor page (~3.2-4.9s measured).
 *
 * Live rather than cached because Stripe's `search` cost is FLAT in the window:
 * 3.2s over 1 day and 3.5s over 90, because it filters server-side and returns
 * one page. The `charges.list` shape the nightly rollup uses walks the whole
 * window instead — 25.7s at 7 days, 129.7s at 30 — which is what made this a
 * real measurement rather than an assumption carried over from the Firestore
 * screens. Caching it would only make a "did this payment just go through"
 * screen staler for no saving.
 */
export const fetchPayments = (
  range: DateRangeKey,
  status: PaymentStatusFilter,
  cursor?: string
) =>
  requestJson<PaymentsResponse>(
    `payments?range=${encodeURIComponent(range)}&status=${encodeURIComponent(status)}` +
      (cursor ? `&cursor=${encodeURIComponent(cursor)}` : "")
  );

/**
 * Top customers by Markit revenue over one trailing window.
 *
 * Unlike the volume history, this one DOES take a window parameter: the board
 * is ordered and limited by Firestore off a different field per window, so the
 * ten rows for "last 30 days" are a different ten rows, not a reslice of the
 * lifetime ten that the browser could do on its own.
 *
 * **`basis` is a refetch for exactly the same reason, and that is worth not
 * "optimising" away.** Net revenue is a stored field with its own Firestore
 * ordering, so the top ten by net is a genuinely different ten people from the
 * top ten by gross — Markit keeps ~96% of a subscription dollar and ~68% of a
 * ticketing one, so a ticketing-heavy creator drops. Netting the gross page in
 * the browser would relabel the columns under a gross ordering, which looks
 * right and is not.
 */
export const fetchLeaderboard = (window: RevenueWindow, basis: RevenueBasis) =>
  requestJson<LeaderboardResponse>(
    `customers/leaderboard?window=${encodeURIComponent(
      window
    )}&basis=${encodeURIComponent(basis)}`
  );

export const fetchCustomerStats = (uid: string) =>
  requestJson<CustomerStatsResponse>(`customers/${encodeURIComponent(uid)}/stats`);

/**
 * Cached — the whole platform message-volume history in one read.
 *
 * Deliberately takes no range parameter. The backing document is one Firestore
 * read whatever slice you want, and the chart's day/week/month toggle regroups
 * the same rows client-side — so a range param would add a round trip per
 * toggle and save nothing.
 *
 * Cached rather than live, unlike `fetchMessaging` above, because a per-day
 * history is a different query from a range total: Twilio's Daily endpoint
 * returns one row per category per day, and Markit's main account has 522
 * categories, so it is only usable filtered to one category at a time. Three
 * filtered calls cover 2022-2026 in ~6.3s — still Twilio's own numbers, just
 * read once a night instead of once a page load.
 */
export const fetchPlatformVolume = () =>
  requestJson<PlatformVolumeResponse>("platform/volume");

/**
 * Cached — realized recurring revenue by day, whole history, in one read.
 *
 * Takes no range for the same reason `fetchPlatformVolume` doesn't: the
 * day/week/month toggle regroups the same rows client-side, so a range param
 * would cost a round trip per toggle and save nothing.
 *
 * **What it is not.** There is no historical MRR series and there cannot be one:
 * `mrrCents` on a rollup row is a point-in-time reading, deliberately absent
 * from every backfilled row (4 of 1,743 carry it) because one measurement
 * stamped across five years is a fabricated line. This returns what Markit
 * actually invoiced each day — baseline plus metered overflow, both populated on
 * every row back to 2021-12-01 — which is a real, complete measurement of a
 * *different* question from the live Estimated MRR projection.
 */
export const fetchPlatformRevenue = () =>
  requestJson<PlatformRevenueResponse>("platform/revenue");

/**
 * Cached — one customer's day-level volume, out of the `byDay` maps the nightly
 * rollup already writes. No new collection backs this; it is a read of data
 * that was being recorded all along.
 */
export const fetchCustomerVolume = (uid: string) =>
  requestJson<CustomerVolumeResponse>(
    `customers/${encodeURIComponent(uid)}/volume`
  );

/** Cached — top senders, sorted and limited by Firestore like the revenue board. */
export const fetchVolumeLeaderboard = (window: VolumeWindow) =>
  requestJson<VolumeLeaderboardResponse>(
    `customers/volume-leaderboard?window=${encodeURIComponent(window)}`
  );

export const fetchSyncState = () => requestJson<SyncStateResponse>("sync/state");

/**
 * Live — one page of an account's stored DM threads, newest first.
 *
 * Only multi-step flows and failed sends are stored, and they expire after 90
 * days. The cursor is the backend's opaque page token, same style as
 * `fetchPayments`.
 */
export const fetchDmThreads = (uid: string, igAccountId: string, cursor?: string) =>
  requestJson<DmThreadsResponse>(
    `engagement/instagram/dms/threads?uid=${encodeURIComponent(
      uid
    )}&igAccountId=${encodeURIComponent(igAccountId)}` +
      (cursor ? `&cursor=${encodeURIComponent(cursor)}` : "")
  );

/** Live — one page of a thread's messages, newest first. */
export const fetchDmMessages = (
  uid: string,
  igAccountId: string,
  igUserId: string,
  cursor?: string
) =>
  requestJson<DmMessagesResponse>(
    `engagement/instagram/dms/messages?uid=${encodeURIComponent(
      uid
    )}&igAccountId=${encodeURIComponent(igAccountId)}&igUserId=${encodeURIComponent(
      igUserId
    )}` + (cursor ? `&cursor=${encodeURIComponent(cursor)}` : "")
  );

/** Live — when each account last got a stored DM, for sorting and labelling the account list. */
export const fetchDmAccountActivity = () =>
  requestJson<DmAccountActivityResponse>("engagement/instagram/dms/activity");

/**
 * Looks up the fan username of a thread that has none. Called once when an admin opens such a
 * thread: the backend makes at most one Instagram call and stores the result on the thread.
 * Resolves with no username when Instagram won't give one.
 */
export const fetchDmThreadUsername = (uid: string, igAccountId: string, igUserId: string) =>
  requestJson<{ igUsername?: string }>(
    `engagement/instagram/dms/thread-username?uid=${encodeURIComponent(uid)}` +
      `&igAccountId=${encodeURIComponent(igAccountId)}` +
      `&igUserId=${encodeURIComponent(igUserId)}`,
    "POST"
  );

/** Live — the most recent anomaly flags across every creator, newest first. */
export const fetchDmFlags = (cursor?: string) =>
  requestJson<DmFlagsResponse>(
    "engagement/instagram/dms/flags" +
      (cursor ? `?cursor=${encodeURIComponent(cursor)}` : "")
  );
